import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

export type ConnectivityStatus = 'online' | 'unreachable' | 'offline';

interface ConnectivityContextType {
  status: ConnectivityStatus;
  isOnline: boolean; // true only if 'online'
  checkReachability: () => Promise<void>;
}

const ConnectivityContext = createContext<ConnectivityContextType | undefined>(undefined);

/**
 * Provider component that manages the three-tiered connectivity state (Network, API, and Heartbeat).
 * 
 * Tiers:
 * 1. Network Status (window.online/offline)
 * 2. API Monitoring (Axios interceptor custom events)
 * 3. Heartbeat (Periodic /health ping)
 */
export const ConnectivityProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [status, setStatus] = useState<ConnectivityStatus>(
    navigator.onLine ? 'online' : 'offline'
  );

  const updateStatus = useCallback((newStatus: ConnectivityStatus) => {
    setStatus(newStatus);
  }, []);

  const checkReachability = useCallback(async () => {
    if (!navigator.onLine) {
      updateStatus('offline');
      return;
    }

    try {
      // Lightweight health check
      await axios.get('/health', { timeout: 5000 });
      updateStatus('online');
    } catch (error) {
      // If we are navigator.onLine but the request failed, it's unreachable
      if (navigator.onLine) {
        updateStatus('unreachable');
      } else {
        updateStatus('offline');
      }
    }
  }, [updateStatus]);

  useEffect(() => {
    const handleOnline = () => {
      updateStatus('online');
      checkReachability();
    };
    const handleOffline = () => updateStatus('offline');

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    const handleCheckEvent = () => checkReachability();
    window.addEventListener('check-reachability', handleCheckEvent);

    // Initial check
    checkReachability();

    // Tier 3: Heartbeat mechanism (Interval ping to /health)
    const interval = setInterval(checkReachability, 45000);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('check-reachability', handleCheckEvent);
      clearInterval(interval);
    };
  }, [checkReachability, updateStatus]);

  return (
    <ConnectivityContext.Provider
      value={{
        status,
        isOnline: status === 'online',
        checkReachability,
      }}
    >
      {children}
    </ConnectivityContext.Provider>
  );
};

/**
 * Hook to access the current connectivity status and reachability helpers.
 */
export const useConnectivity = () => {
  const context = useContext(ConnectivityContext);
  if (context === undefined) {
    throw new Error('useConnectivity must be used within a ConnectivityProvider');
  }
  return context;
};
